<script setup lang="ts">
  import { ref, onMounted, onUnmounted, watch } from 'vue'
  import _fetch from '@/utils/fetch'
  import MessageService from '@components/components/AppMessage/message'
  import { useUserStore } from '@common/stores/userStore'
  import Utf8 from 'crypto-js/enc-utf8'
  import AES from 'crypto-js/aes'
  import ECB from 'crypto-js/mode-ecb'
  import Pkcs7 from 'crypto-js/pad-pkcs7'
  import { lockScroll, unlockScroll } from '@common/utils/scrollLock'
  import AppLoading from '@components/components/AppLoading/AppLoading.vue'
  import { googleIframeLogin } from '@common/utils/thirdLogin'
  import { useTicketStore } from '@common/stores/userTicket'
  import { eventBus } from '@common/utils/eventBus'
  import { useI18n } from 'vue-i18n'
  import useLanguage from '@common/hooks/useLanguage'

  const props = defineProps({
    type: {
      type: String,
      required: false,
      default: 'login'
    },
    canClose: {
      type: Boolean,
      default: true
    }
  })
  const { t } = useI18n()
  const emit = defineEmits(['close', 'ok'])
  const $message = MessageService
  const loginType = ref(props.type)
  const step = ref('types')
  const email = ref('')
  const emailCode = ref('')
  const password = ref('')
  const confirmPassword = ref('')
  const nickname = ref('')
  let signing = ref(false)
  const publicKey = '***_shda***_shda'
  const userStore = useUserStore()
  const loading = ref(false)
  const privacyPolicy = ref(false)
  const passwordReg = /^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d\W_]{6,20}$/ // 6-20位数字、字母组合
  const ticketStore = useTicketStore()

  onMounted(() => {
    lockScroll()
  })
  onUnmounted(() => {
    unlockScroll()
  })
  /**
   * AES加密
   */
  function encryptAes(data: string): string {
    const aesKey = publicKey
    if (aesKey && data) {
      const key = Utf8.parse(aesKey)
      const result = AES.encrypt(data, key, {
        mode: ECB,
        padding: Pkcs7
      }).toString()
      return result.replace(/\+/g, '{cand}')
    } else {
      return data
    }
  }
  const emailLogin = async () => {
    if (signing.value) return
    if (!privacyPolicy.value) return
    if (email.value === '' || password.value === '') {
      $message({
        message: t('appLogin.email_password_empty'),
        type: 'error'
      })
      return
    }
    signing.value = true
    const response = await _fetch('/aigc/api/auth/login/email', {
      method: 'POST',
      body: JSON.stringify({
        email: email.value,
        password: encryptAes(password.value)
      })
    })
    signing.value = false
    const res = await response.json()
    if (res.code === 200) {
      // userStore.setUser(res.data);
      // emit('ok', res.data);
      // window.isLoggedIn = true;
      // window.dataLayer && window.dataLayer.push({
      //     'user_name': res.data?.nickName || '',
      //     'userId': res.data?.userId || '',
      //     'from_link': sessionStorage.getItem('ht_source') || '',
      // })
      // window.gtag && window.gtag('event', 'login_success', {
      //     'user_name': res.data?.nickName || '',
      //     'userId': res.data?.userId || '',
      //     'from_link': sessionStorage.getItem('ht_source') || '',
      // });
      loginCallback(res)
    } else {
      $message({
        message: res.msg,
        type: 'error'
      })
    }
  }

  const emailRegister = async () => {
    if (signing.value) return
    if (
      email.value === '' ||
      emailCode.value === '' ||
      password.value === '' ||
      confirmPassword.value === '' ||
      nickname.value === ''
    ) {
      $message({
        message: t('appLogin.email_password_nick_empty'),
        type: 'error'
      })
      return
    }
    if (password.value !== confirmPassword.value) {
      $message({
        message: t('appLogin.password_not_match'),
        type: 'error'
      })
      return
    }
    if (!checkPassword()) return
    signing.value = true
    const response = await _fetch('/aigc/api/auth/register/email', {
      method: 'POST',
      body: JSON.stringify({
        email: email.value,
        password: encryptAes(password.value),
        nickname: nickname.value,
        code: emailCode.value,
        fromLink: sessionStorage.getItem('ht_source') || ''
      })
    })
    signing.value = false
    const res = await response.json()
    if (res.code === 200) {
      // userStore.setUser(res.data);
      // emit('ok', res.data);
      // window.isLoggedIn = true;
      // window.dataLayer && window.dataLayer.push({
      //     'user_name': res.data?.nickName || '',
      //     'userId': res.data?.userId || '',
      //     'from_link': sessionStorage.getItem('ht_source') || '',
      // })
      // window.gtag && window.gtag('event', 'sign_up_success', {
      //     'user_name': res.data?.nickName || '',
      //     'userId': res.data?.userId || '',
      //     'from_link': sessionStorage.getItem('ht_source') || '',
      // });
      loginCallback(res)
    } else {
      $message({
        message: res.msg,
        type: 'error'
      })
    }
  }
  const loginCallback = (res: any, loginType = 'hitems') => {
    userStore.setUser(res.data)
    ticketStore.setTicket(res.data.balance || '0')
    // eslint-disable-next-line no-debugger
    // TODO 后端没有返回ticket，所以临时获取下
    getTicketCount()
    eventBus.trigger('mountNotice')
    emit('ok', res.data)
    window.isLoggedIn = true
    window.dataLayer &&
      window.dataLayer.push({
        user_name: res.data?.nickName || '',
        userId: res.data?.userId || '',
        from_link: sessionStorage.getItem('ht_source') || '',
        login_type: loginType
      })
    window.gtag &&
      window.gtag('event', 'login_success', {
        user_name: res.data?.nickName || '',
        userId: res.data?.userId || '',
        from_link: sessionStorage.getItem('ht_source') || '',
        login_type: loginType
      })
    window.sensors &&
      window.sensors.setProfile({
        userId: res.data?.userId || '',
        user_name: res.data?.nickName || ''
      })
  }
  const getTicketCount = async () => {
    const response = await _fetch('/aigc/api/user/account')
    const res = await response.json()
    if (res.code === 200) {
      ticketStore.setTicket(res.data)
    }
  }
  watch(
    () => loginType.value,
    () => {
      email.value = ''
      emailCode.value = ''
      password.value = ''
      confirmPassword.value = ''
      nickname.value = ''
    }
  )
  const ifSendCode = ref(false)
  const codeTime = ref(60)
  let sendCodeTimer: ReturnType<typeof setInterval> | null = null
  // 校验密码
  const checkPassword = () => {
    if (!passwordReg.test(password.value)) {
      $message({
        message: t('appLogin.password_too_long'),
        type: 'error'
      })
      return false
    }
    return true
  }
  const sendEmailCode = async (type = '') => {
    if (ifSendCode.value) return
    if (email.value === '') {
      $message({
        message: t('appLogin.email_empty'),
        type: 'error'
      })
      return
    }
    ifSendCode.value = true
    await _fetch('/aigc/api/auth/register/email/code', {
      method: 'GET',
      params: {
        email: email.value,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        type: type
      }
    })
    countdown()
  }
  const countdown = () => {
    sendCodeTimer = setInterval(() => {
      if (codeTime.value > 0) {
        codeTime.value--
      } else {
        ifSendCode.value = false
        codeTime.value = 60
        sendCodeTimer && clearInterval(sendCodeTimer)
      }
    }, 1000)
  }
  // 密码显示隐藏
  const handlePassword = (event: Event) => {
    event.stopPropagation()
    const target = event.target as HTMLInputElement
    const pre = target.previousSibling as HTMLInputElement
    if (pre) {
      const type = pre.type
      if (type === 'password') {
        pre.type = 'text'
        target.className = 'password-icon close'
      } else {
        pre.type = 'password'
        target.className = 'password-icon'
      }
    }
  }

  const resetPassword = async () => {
    if (signing.value) return
    if (
      email.value === '' ||
      emailCode.value === '' ||
      password.value === '' ||
      confirmPassword.value === ''
    ) {
      $message({
        message: t('appLogin.email_code_password_empty'),
        type: 'error'
      })
      return
    }
    if (password.value !== confirmPassword.value) {
      $message({
        message: t('appLogin.password_not_match'),
        type: 'error'
      })
      return
    }
    if (!checkPassword()) return
    signing.value = true
    const response = await _fetch('/aigc/api/auth/forgot', {
      method: 'POST',
      body: JSON.stringify({
        email: email.value,
        password: encryptAes(password.value),
        matchingPassword: encryptAes(confirmPassword.value),
        code: emailCode.value
      })
    })
    signing.value = false
    const res = await response.json()
    if (res.code === 200) {
      $message({
        message: res.msg,
        type: 'success'
      })
      loginType.value = 'login'
    } else {
      $message({
        message: res.msg,
        type: 'error'
      })
    }
  }

  const _env_ = window._ENV_ || 'CN'

  onMounted(() => {
    googleIframeLogin(loginCallback)
  })

  onUnmounted(() => {
    sendCodeTimer && clearInterval(sendCodeTimer)
  })
</script>
<template>
  <main class="login-mask-container" id="login_container">
    <div class="login-container" @click.stop="() => {}">
      <a v-if="props.canClose" class="detail-close" @click.stop="!loading && $emit('close')">
        <span data-v-1f531508="" class="hitems-close"></span>
      </a>
      <div class="login-header">
        <img
          v-if="_env_ === 'EN'"
          class="login-logo"
          src="@/assets/images/logo.webp"
          height="32"
          alt="logo"
        />
        <img v-else class="login-logo" src="@/assets/images/logo-cn.webp" height="36" alt="logo" />
      </div>
      <div v-if="loginType !== 'reset'" class="login-types">
        <!-- <a @click.stop="beginGoogleLogin" class="login-btn login-btn-types login-btn-google">
                    Continue With Google
                </a> -->
        <div id="google_customBtn" class="login-btn login-btn-types login-btn-google">
          {{ $t('appLogin.continue_with_google') }}
        </div>
        <!-- <a class="login-btn login-btn-types login-btn-facebook">
                    Continue With Facebook
                </a> -->
        <!-- <a class="login-btn login-btn-types login-btn-email" @click="step = 'email'">
                    Continue With Email
                </a> -->
        <div v-if="loading" class="loading-content">
          <AppLoading />
        </div>
      </div>
      <div v-if="loginType !== 'reset'" class="login-line">
        <span class="login-line-text">{{ $t('appLogin.or') }}</span>
      </div>

      <template v-if="loginType === 'register'">
        <div class="login-types">
          <div class="login-btn">
            <input
              v-model="email"
              @keyup.enter="emailRegister"
              name="username"
              class="login-input"
              type="text"
              :placeholder="$t('appLogin.email')"
            />
          </div>
          <div class="login-btn">
            <input
              v-model="emailCode"
              class="login-input login-input-code"
              type="text"
              :placeholder="$t('appLogin.email_code')"
            />
            <a
              :class="[
                'login-btn',
                'login-btn-email-code',
                { zh: !useLanguage().isEnglish },
                `${ifSendCode ? 'disabled' : ''}`
              ]"
              @click="sendEmailCode()"
            >
              {{ ifSendCode ? `${codeTime}${$t('appLogin.s')}` : $t('appLogin.send') }}
            </a>
          </div>
          <div class="login-btn">
            <input
              v-model="password"
              @keyup.enter="emailRegister"
              name="password"
              class="login-input"
              type="password"
              :placeholder="$t('appLogin.password')"
            />
            <a class="password-icon" @click="handlePassword"></a>
          </div>
          <div class="login-btn">
            <input
              v-model="confirmPassword"
              @keyup.enter="emailRegister"
              class="login-input"
              type="password"
              :placeholder="$t('appLogin.confirm_password')"
            />
            <a class="password-icon" @click="handlePassword"></a>
          </div>
          <div class="login-btn">
            <input
              v-model="nickname"
              @keyup.enter="emailRegister"
              class="login-input"
              type="text"
              :placeholder="$t('appLogin.nickname')"
            />
          </div>
          <a
            :class="{
              'login-btn': true,
              'login-btn-sign': true,
              'zh': !useLanguage().isEnglish,
              'signing': signing
            }"
            @click="emailRegister"
          >
            {{ $t('appLogin.sign_up') }}
            <div class="login-tips">{{ $t('appLogin.want_ticket') }}</div>
          </a>
        </div>
      </template>
      <template v-if="loginType === 'login'">
        <div class="login-types">
          <div class="login-btn">
            <input
              v-model="email"
              @keyup.enter="emailLogin"
              name="username"
              class="login-input"
              type="text"
              :placeholder="$t('appLogin.email')"
            />
          </div>
          <div class="login-btn">
            <input
              v-model="password"
              @keyup.enter="emailLogin"
              name="password"
              class="login-input"
              type="password"
              :placeholder="$t('appLogin.password')"
            />
            <a class="password-icon" @click="handlePassword"></a>
          </div>
          <a
            :class="{
              'login-btn': true,
              'login-btn-sign': true,
              'signing': signing,
              'zh': !useLanguage().isEnglish,
              'disabled': !privacyPolicy
            }"
            @click="emailLogin"
          >
            {{ $t('appLogin.sign_in') }}
            <!-- <div class="login-tips">+1 want-ticket</div> -->
          </a>
          <label class="privacy-policy">
            <input
              type="radio"
              v-model="privacyPolicy"
              value="true"
              class="privacy-policy-checkbox"
            />
            <div>
              {{ $t('appLogin.i_agreed') }}&nbsp;
              <a href="/terms" target="_blank">{{ $t('appLogin.terms_of_service') }}</a>
              {{ $t('appLogin.and') }} &nbsp;
              <a href="/privacy-policy" target="_blank">{{ $t('appLogin.privacy_polivy') }}</a>
              {{ $t('appLogin..') }}
            </div>
          </label>
        </div>
      </template>
      <template v-if="loginType === 'reset'">
        <div class="login-types">
          <div class="login-btn">
            <input
              v-model="email"
              @keyup.enter="resetPassword"
              name="username"
              class="login-input"
              type="text"
              :placeholder="$t('appLogin.email')"
            />
          </div>
          <div class="login-btn">
            <input
              v-model="emailCode"
              class="login-input login-input-code"
              type="text"
              :placeholder="$t('appLogin.email_code')"
            />
            <a
              :class="[
                'login-btn',
                'login-btn-email-code',
                { zh: !useLanguage().isEnglish },
                `${ifSendCode ? 'disabled' : ''}`
              ]"
              @click="sendEmailCode('FORGET_PASSWORD')"
            >
              {{ ifSendCode ? `${codeTime}${$t('appLogin.s')}` : $t('appLogin.send') }}
            </a>
          </div>
          <div class="login-btn">
            <input
              v-model="password"
              @keyup.enter="resetPassword"
              name="password"
              class="login-input"
              type="password"
              :placeholder="$t('appLogin.password')"
            />
            <a class="password-icon" @click="handlePassword"></a>
          </div>
          <div class="login-btn">
            <input
              v-model="confirmPassword"
              @keyup.enter="resetPassword"
              class="login-input"
              type="password"
              :placeholder="$t('appLogin.confirm_password')"
            />
            <a class="password-icon" @click="handlePassword"></a>
          </div>
          <a
            :class="{
              'login-btn': true,
              'login-btn-sign': true,
              'signing': signing,
              'zh': !useLanguage().isEnglish
            }"
            @click="resetPassword"
          >
            {{ $t('appLogin.reset') }}
          </a>
        </div>
      </template>
      <div v-if="loginType !== 'reset'" :class="['login-footer', { zh: !useLanguage().isEnglish }]">
        <template v-if="loginType === 'register'">
          {{ $t('appLogin.confirm_account') }}
          <br v-if="useLanguage().isEnglish" />
          <p class="login-tab">
            <a @click="loginType = 'login'">{{ $t('appLogin.sign_in') }}</a>
          </p>
        </template>
        <template v-if="loginType === 'login'">
          {{ $t('appLogin.no_account') }}
          <br v-if="useLanguage().isEnglish" />
          <p class="login-tab">
            <a @click="loginType = 'register'">{{ $t('appLogin.sign_up') }}</a>
          </p>
        </template>
        <br v-if="useLanguage().isEnglish" />
        <span v-if="!useLanguage().isEnglish && loginType === 'login'">|</span>
        <div v-if="loginType === 'login'">
          {{ $t('appLogin.need_help') }}
          <br v-if="useLanguage().isEnglish" />
          <p class="login-tab">
            <a @click="loginType = 'reset'">{{ $t('appLogin.forgot_password') }}</a>
          </p>
        </div>
      </div>
    </div>
  </main>
</template>
<style lang="stylus" scoped>
  .login-mask-container
      display flex
      align-items center
      justify-content center
      width 100%
      height 100vh
      background-color rgba(0, 0, 0, .8)
      position fixed
      top 0
      left 0
      bottom 0
      z-index 999
  .login-container
      display flex
      flex-direction column
      width 420px
      // height 700px
      border-radius 6px
      background-color #fff
      box-shadow 0px 4px 10px 0px rgba(0, 0, 0, 0.3)
      padding 30px 50px
      position relative
      .privacy-policy
          font-size 12px
          line-height 1.5
          color #666
          display: flex
          align-items: start
          .privacy-policy-checkbox
              margin-top 2px
              margin-right 0.6rem
      .detail-close
          display: flex
          align-items: center;
          justify-content: center;
          position: absolute;
          width: 4rem;
          height: 4rem;
          top: 0;
          right: -4.6rem;
          border-radius: 50%;
          font-size: 2rem;
          font-weight: 700;
          background-color: #fff;
          transition: all .2s ease-in-out;
          &:hover
              background-color: #fffc
      .login-header
          width 100%
          height 90px
          display flex
          align-items center
          justify-content center
          margin-bottom 2rem
      // .login-logo
      //     position relative
      //     left 20px
      .login-types
          display flex
          flex-direction column
          // margin-top 30px
          position relative
          .loading-content
              position absolute
              width 100%
              height 100%
              top 0
              left 0
              bottom 0
              z-index 2
              background-color rgba(255, 255, 255, .4)
              display flex
              align-items center
              justify-content center
          .login-btn
              display flex
              align-items center
              justify-content center
              width 100%
              height 40px
              border-radius 30px
              margin-bottom 20px
              background-color #F6F6FF
              font-size 18px
              font-weight 500
              color #333
              background-repeat no-repeat
              background-position 10px center
              background-size 22px
              transition all .2s ease-in-out
              position relative
              &.login-btn-types
                  margin 0
                  &:hover
                      background-color #EAEAFF
              &.login-btn-google
                  background-image url('@/assets/images/google.webp')
              &.login-btn-facebook
                  background-image url('@/assets/images/facebook.webp')
              &.login-btn-email
                  background-image url('@/assets/images/email.webp')
              &.login-btn-sign
                  background-color var(--primary-color)
                  color #fff
                  margin-bottom 1rem
                  &.zh
                      font-size 1.6rem
                  &.signing
                      background-color #999
                      cursor not-allowed
              &.zh
                  font-size 1.6rem
              &.login-btn-email-code
                  width 80px
                  background-color var(--primary-color)
                  color #fff
                  margin 0 0 0 10px
                  border-top-left-radius 0
                  border-bottom-left-radius 0
                  &.zh
                      font-size 1.6rem
                  &.disabled
                      background-color #999
                      cursor not-allowed
              &.disabled
                  background-color #6D5DFF
                  cursor not-allowed
              .login-tips
                  position absolute
                  display flex
                  align-items center
                  padding 0.8rem 1.6rem
                  border-radius: 0.8rem
                  background: rgba(16, 16, 16, 0.8)
                  color #101010
                  font-size 1.4rem
                  top: -1.2rem
                  right 0
                  background: rgba(255, 255, 255, 0.6);
                  backdrop-filter: blur(8px);
                  .login-tips-icon
                      margin-left 0.4rem
  .login-line
      width 100%
      border-bottom 1px solid rgba(207, 219, 213, 0.6)
      position relative
      margin 4rem 0
      .login-line-text
          font-size 1.2rem
          color #5D5D5B
          background-color #fff
          padding 0 10px
          position absolute
          left 50%
          top 50%
          transform translate(-50%, -50%)
  .login-footer
      font-size 14px
      font-weight 500
      text-align center
      margin-top 80px
      color #D8D8D8
      font-weight 300
      line-height 20px
      &.zh
          color:#878787
          margin-top 7.4rem
          display: flex
          justify-content: center;
      .login-footer-light
          font-weight 500
          color #76DF57
  .login-input
      flex 1
      background-color transparent
      box-shadow none
      border none
      font-size 16px
      padding 0 20px
      color #333
  .password-icon
      display inline-block
      width 20px
      height 16px
      background url('@/assets/images/password-close.webp') no-repeat center center
      background-size 18px
      margin-right 10px
      &.close
          background url('@/assets/images/password.webp') no-repeat center center
          background-size 18px
  .login-tab
      text-align center
      color #76DF57
      margin 0 0.5rem
      font-weight 400
      font-size 1.6rem
      &.zh{
          font-weight 400
          font-size 1.6rem
      }
      span
          1.2rem
          &.zh{
              font-weight:400
          }


  @media screen and (max-width: 500px)
      .login-mask-container
          height calc(var(--vh, 1vh) * 100)
          .login-container
              width 100%
              height calc(var(--vh, 1vh) * 63)
              position absolute
              bottom 0
              left 0
              align-items center
              padding 3rem 3.5rem 1rem
              overflow-y auto
              .detail-close
                  bottom: auto;
                  top: 10px;
                  right: 4px;
              .login-footer
                  margin-top 3rem
              .login-header
                  height auto
                  width auto
                  margin-bottom 30px
                  .login-logo
                      left 0px
                      height 2.8rem
              .login-line
                  margin 30px 0
              .login-types
                  // margin-top 3rem
                  width 100%
                  .login-btn
                      // height 3.2rem
                      margin-bottom 1.2rem
                      .login-btn-email-code
                          margin-bottom 0 !important
                          width 7rem
                      .login-input-code
                          width calc(100% - 7rem)
  @media screen and (min-width: 501px) and (max-width: 1024px)
      .login-mask-container
          height calc(var(--vh, 1vh) * 100)
          .login-container
              width 360px
              height auto
              align-items center
              padding 3.5rem
              .detail-close
                  bottom: -50px;
                  top: auto;
                  right: auto;
              .login-footer
                  margin-top 1rem
              .login-header
                  height auto
                  width auto
                  margin-bottom 30px
                  .login-logo
                      left 0px
                      height 2.8rem
              .login-types
                  // margin-top 3rem
                  width 100%
                  .login-btn
                      height 3.5rem
                      margin-bottom 1.2rem
                      &.login-btn-types
                          margin 0
                      .login-btn-email-code
                          margin-bottom 0 !important
                          width 7rem
                      .login-input-code
                          width calc(100% - 7rem)
              .login-line
                  margin 30px 0
</style>
